import { createJSONStorage } from 'zustand/middleware';
import { getUser } from '@frontend/auth-helpers';
import { createShallowStore, createStoreWithPersistAndSubscribe } from '@frontend/store';
import { Verticals } from '../utils/constants';
import type { ColumnFilterValues } from '../utils/typings';

interface IntegrationsDashboardState {
  isUserAuthenticated: boolean;
  isUserLoggingIn: boolean;
  isUserRefreshing: boolean;
  // Map of migration id -> `migratedAt` timestamp. Hydrated from the
  // ListMigrations response because GetMigrationByID does not return this field.
  migratedAtById: Record<string, string | undefined>;
  dashboardSelectedVertical: Verticals;
  dashboardSearchQuery: string;
  dashboardColumnFilters: ColumnFilterValues;
  dashboardShowHiddenIntegrations: boolean;
}

interface IntegrationsDashboardActions {
  setIsUserAuthenticated: (isAuthenticated: boolean) => void;
  setIsUserLoggingIn: (isClicked: boolean) => void;
  setIsUserRefreshing: (isRefreshing: boolean) => void;
  setMigratedAtEntries: (entries: Array<{ id?: string; migratedAt?: string }>) => void;
  setDashboardSelectedVertical: (vertical: Verticals) => void;
  setDashboardSearchQuery: (query: string) => void;
  setDashboardColumnFilters: (filters: ColumnFilterValues) => void;
  setDashboardShowHiddenIntegrations: (show: boolean) => void;
  resetState: () => void;
}

type IntegrationsDashboardStore = IntegrationsDashboardState & IntegrationsDashboardActions;

const initialState: IntegrationsDashboardState = {
  isUserAuthenticated: false, //True if user is authenticated, false otherwise.
  isUserLoggingIn: false, //True when login process starts, false when it ends.
  isUserRefreshing: false, //True when user triggers a data refresh, false when it ends.
  migratedAtById: {},
  dashboardSelectedVertical: Verticals.ALL,
  dashboardSearchQuery: '',
  dashboardColumnFilters: {},
  dashboardShowHiddenIntegrations: false,
};

const userPreferenceDefaults: Pick<
  IntegrationsDashboardState,
  'dashboardSelectedVertical' | 'dashboardSearchQuery' | 'dashboardColumnFilters' | 'dashboardShowHiddenIntegrations'
> = {
  dashboardSelectedVertical: initialState.dashboardSelectedVertical,
  dashboardSearchQuery: initialState.dashboardSearchQuery,
  dashboardColumnFilters: initialState.dashboardColumnFilters,
  dashboardShowHiddenIntegrations: initialState.dashboardShowHiddenIntegrations,
};

const STORAGE_KEY = 'IntegrationsDashboard';
// Pre-userId key; drop it so old shared data can't leak into any account.
localStorage.removeItem(STORAGE_KEY);

// The user the persisted data belongs to; only changes via syncIntegrationsDashboardStoreUser.
let storageUserId: string | undefined = getUser()?.userID;
const userScopedKey = (name: string) => (storageUserId ? `${name}:${storageUserId}` : undefined);

const userScopedStorage = createJSONStorage<IntegrationsDashboardStore>(() => ({
  getItem: (name) => {
    const key = userScopedKey(name);
    return key ? localStorage.getItem(key) : null;
  },
  setItem: (name, value) => {
    const key = userScopedKey(name);
    if (key) localStorage.setItem(key, value);
  },
  removeItem: (name) => {
    const key = userScopedKey(name);
    if (key) localStorage.removeItem(key);
  },
}));

export const useIntegrationsDashboardStore = createStoreWithPersistAndSubscribe<IntegrationsDashboardStore>(
  (set) => ({
    ...initialState,
    setIsUserAuthenticated: (isAuthenticated: boolean) => {
      set({ isUserAuthenticated: isAuthenticated });
    },
    setIsUserLoggingIn: (isClicked: boolean) => {
      set({ isUserLoggingIn: isClicked });
    },
    setIsUserRefreshing: (isRefreshing: boolean) => {
      set({ isUserRefreshing: isRefreshing });
    },
    setMigratedAtEntries: (entries) => {
      set((state) => {
        const next = { ...state.migratedAtById };
        let changed = false;
        for (const { id, migratedAt } of entries) {
          if (!id) continue;
          if (next[id] !== migratedAt) {
            next[id] = migratedAt;
            changed = true;
          }
        }
        return changed ? { migratedAtById: next } : state;
      });
    },
    setDashboardSelectedVertical: (vertical: Verticals) => {
      set({ dashboardSelectedVertical: vertical });
    },
    setDashboardSearchQuery: (query: string) => {
      set({ dashboardSearchQuery: query });
    },
    setDashboardColumnFilters: (filters: ColumnFilterValues) => {
      set({ dashboardColumnFilters: filters });
    },
    setDashboardShowHiddenIntegrations: (show: boolean) => {
      set({ dashboardShowHiddenIntegrations: show });
    },
    resetState: () => {
      set(initialState);
    },
  }),
  {
    name: STORAGE_KEY,
    storage: userScopedStorage,
    // Fall back to defaults so a user without saved data doesn't inherit the previous user's preferences.
    merge: (persisted, current) => ({
      ...current,
      ...userPreferenceDefaults,
      ...(persisted as Partial<IntegrationsDashboardStore> | undefined),
    }),
  },
  {
    name: 'IntegrationsDashboardStore',
    enabled: true,
  }
);

export const useIntegrationsDashboardShallowStore =
  createShallowStore<IntegrationsDashboardStore>(useIntegrationsDashboardStore);

/** Points persistence at the currently signed-in user and loads their saved data. */
export const syncIntegrationsDashboardStoreUser = async () => {
  const userId = getUser()?.userID;
  if (userId === storageUserId) return;
  storageUserId = userId;
  await useIntegrationsDashboardStore.persist.rehydrate();
};

/** Wipes the signed-in user's dashboard data from memory and localStorage. */
export const clearIntegrationsDashboardStore = () => {
  useIntegrationsDashboardStore.getState().resetState();
  useIntegrationsDashboardStore.persist.clearStorage();
  storageUserId = undefined;
};
